import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import Card from "antd/es/card";
import Empty from "antd/es/empty";
import Segmented from "antd/es/segmented";
import Spin from "antd/es/spin";
import { sortBy } from "lodash";
import * as Sentry from "@sentry/react";
import "./calendar.css";
import { CALENDAR_COLORS, COLORS, CUT_OFF, monthFilters } from "./constants";
import { Period } from "./utils";
import api from "./api";
import { logOut, genericError } from "./Dashboard";
import dayjs from "dayjs";

const { REACT_APP_API_URL: API_URL, REACT_APP_BRAND_NAME } = process.env;

dayjs().format();

export const white = "#ffffff";
export const green = "#4aa96c";
export const red = "#f55c47";
export const gray = "#aaaaaa";
export const blue = "#1e90ff";
export const yellow = "#ffdf6b";

export default function Calendar({ bookings, period, selected }) {
  const intl = useIntl();
  const [viewMode, setViewMode] = useState("monthly");
  const [yearlyBookings, setYearlyBookings] = useState([]);
  const [yearlyBookingsKey, setYearlyBookingsKey] = useState(null);
  const [yearlyLoading, setYearlyLoading] = useState(false);

  useEffect(() => {
    if (viewMode !== "yearly" || !selected) return;
    const key = `${selected}-${period.year}`;
    if (key === yearlyBookingsKey || yearlyLoading) return;

    const cutOff = period.isQuarterly()
      ? CUT_OFF[REACT_APP_BRAND_NAME]?.QUARTERLY
      : CUT_OFF[REACT_APP_BRAND_NAME]?.MONTHLY;
    const cutOffStartDate = cutOff
      ? `${cutOff.year}-${String(cutOff.month).padStart(2, "0")}-01`
      : null;
    const yearStartDate = `${period.year}-01-01`;
    const startDate =
      cutOffStartDate && cutOffStartDate > yearStartDate
        ? cutOffStartDate
        : yearStartDate;
    const endDate = `${period.year}-12-31`;

    Sentry.logger.debug("Loading yearly calendar bookings", {
      rentalId: selected,
      year: period.year,
    });

    setYearlyLoading(true);
    api({
      method: "get",
      url: `${API_URL}/costs/portal/calendar-bookings?bookingsync_rental_id=${selected}&start_date=${startDate}&end_date=${endDate}`,
      headers: { Authorization: `Bearer ${localStorage.token}` },
    })
      .then((response) => {
        const data =
          (response && response.data && response.data.bookings) || [];
        setYearlyBookings(data);
        setYearlyBookingsKey(key);
        Sentry.logger.info("Yearly calendar bookings loaded", {
          rentalId: selected,
          year: period.year,
          bookingsCount: data.length,
        });
      })
      .catch((error) => {
        const status = error.response?.status;
        if (status === 401) {
          logOut(intl);
        } else {
          Sentry.logger.error("Failed to load yearly calendar bookings", {
            error: error.message,
            rentalId: selected,
            year: period.year,
          });
          genericError(intl, error, {
            endpoint: "/costs/portal/calendar-bookings",
          });
        }
      })
      .finally(() => {
        setYearlyLoading(false);
      });
  }, [viewMode, selected, period.year, yearlyBookingsKey, yearlyLoading, intl]);

  function resolveBookingType(booking) {
    if (booking && booking.status) return booking.status;
    const comments = (booking && booking.comments) || "";
    if (comments.includes("Pobyt właścicielski (niepotwierdzony)"))
      return "owner-tentative";
    if (comments.includes("Pobyt właścicielski")) return "owner";
    if (comments.includes("Blokada terminu")) return "unavailable";
    return "booked";
  }

  const renderedPeriods =
    viewMode === "yearly"
      ? monthFilters.map((m) => new Period(m, period.year))
      : period.getPeriods();

  const bookingsToRender =
    viewMode === "yearly" ? sortBy(yearlyBookings, "from_date") : bookings;

  function isCurrent(p) {
    if (Number(p.year) !== Number(period.year)) return false;
    if (period.isQuarterly()) {
      return period.getPeriods().some((q) => q.month.id === p.month.id);
    }
    return p.month.id === period.month.id;
  }

  function setStyle(row) {
    const brandColors = CALENDAR_COLORS[REACT_APP_BRAND_NAME];
    let background;

    function resolveColor(type) {
      return brandColors[type] || brandColors["default"];
    }

    if (row.label || row.dayOfMonth === "-") {
      background = white;
    } else if (row.booked) {
      if (row.types[0] || row.types[1]) {
        if (viewMode === "yearly") {
          if (row.isBookingEnd && !row.isBookingStart) {
            background = brandColors["available"];
          } else {
            const solidType =
              row.types[1] && row.types[1] !== "available"
                ? row.types[1]
                : row.types[0];
            background = resolveColor(solidType);
          }
        } else if (row.isBookingEnd || row.isBookingStart) {
          background = `linear-gradient(150deg, ${resolveColor(
            row.types[0]
          )} 49%, ${white} 50%, ${resolveColor(row.types[1])} 51%)`;
        } else {
          background = `linear-gradient(150deg, ${resolveColor(
            row.types[0]
          )} 50%, ${resolveColor(row.types[1])} 50%)`;
        }
      } else {
        background = brandColors["booked"];
      }
    } else {
      background = brandColors["available"];
    }

    return {
      width: "14.285%",
      textAlign: "center",
      background,
    };
  }

  function renderPeriodCalendar(period, bookingsForPeriod, compact) {
    const data = [];
    const startDay = dayjs()
      .month(period.month.id - 1)
      .year(period.year)
      .startOf("month")
      .endOf("day");
    const endDay = dayjs()
      .month(period.month.id - 1)
      .year(period.year)
      .endOf("month")
      .endOf("day");

    let day = startDay;

    // push the labels
    data.push(
      {
        dayOfWeek: 1,
        dayOfMonth: intl.formatMessage({ id: "mon" }),
        label: true,
      },
      {
        dayOfWeek: 2,
        dayOfMonth: intl.formatMessage({ id: "tue" }),
        label: true,
      },
      {
        dayOfWeek: 3,
        dayOfMonth: intl.formatMessage({ id: "wed" }),
        label: true,
      },
      {
        dayOfWeek: 4,
        dayOfMonth: intl.formatMessage({ id: "thu" }),
        label: true,
      },
      {
        dayOfWeek: 5,
        dayOfMonth: intl.formatMessage({ id: "fri" }),
        label: true,
      },
      {
        dayOfWeek: 6,
        dayOfMonth: intl.formatMessage({ id: "sat" }),
        label: true,
      },
      {
        dayOfWeek: 0,
        dayOfMonth: intl.formatMessage({ id: "sun" }),
        label: true,
      }
    );
    // fill days until first of the month
    let emptyDay = 1;
    while (emptyDay !== day.day()) {
      data.push({ dayOfWeek: day.day(), dayOfMonth: "-" });
      emptyDay = (emptyDay + 1) % 7;
    }
    // stretch the bookings for all days they take up and fill the gaps
    for (let i = 0; i < bookingsForPeriod.length; i++) {
      while (
        day.format() <
          dayjs(bookingsForPeriod[i].from_date).endOf("day").format() &&
        day.format() <= endDay.format()
      ) {
        data.push({
          booked: false,
          dayOfWeek: day.day(),
          dayOfMonth: day.date(),
        });
        day = day.add(1, "day");
      }
      while (
        day.format() <=
          dayjs(bookingsForPeriod[i].until_date).endOf("day").format() &&
        day.format() <= endDay.format()
      ) {
        const isBookingEnd =
          day.format() ===
          dayjs(bookingsForPeriod[i].until_date).endOf("day").format();
        const isBookingStart =
          day.format() ===
          dayjs(bookingsForPeriod[i].from_date).endOf("day").format();
        const isNextBookingStart =
          bookingsForPeriod[i + 1] &&
          day.format() ===
            dayjs(bookingsForPeriod[i + 1].from_date)
              .endOf("day")
              .format();
        data.push({
          booked: true,
          isBookingEnd,
          isBookingStart: isBookingStart || isNextBookingStart,
          types: [
            isBookingEnd || (!isBookingEnd && !isBookingStart)
              ? resolveBookingType(bookingsForPeriod[i])
              : "available",
            isBookingStart || (!isBookingEnd && !isBookingStart)
              ? resolveBookingType(bookingsForPeriod[i])
              : isNextBookingStart
                ? resolveBookingType(bookingsForPeriod[i + 1])
                : "available",
          ],
          dayOfWeek: day.day(),
          dayOfMonth: day.date(),
        });
        day = day.add(1, "day");
      }
    }
    // fill the rest of the month
    while (day.format() <= endDay.format()) {
      data.push({ dayOfMonth: day.date() });
      day = day.add(1, "day");
    }
    // fill the rest of the calendar with dashes
    for (
      let i =
        43 -
        startDay.day() -
        dayjs()
          .month(period.month.id - 1)
          .year(period.year)
          .daysInMonth();
      i > 0;
      i--
    )
      data.push({ dayOfMonth: "-" });
    return (
      <>
        {renderedPeriods.length > 1 ? (
          <div
            style={{
              textAlign: "center",
              padding: compact ? 8 : 20,
              fontSize: compact ? 16 : 20,
              fontWeight: "bold",
            }}
          >
            {compact
              ? intl.formatMessage({ id: `month-${period.month.id}` })
              : period.toString()}
          </div>
        ) : null}
        <Card>
          {data.map((row, index) => (
            <Card.Grid hoverable={false} style={setStyle(row)} key={index}>
              {[
                intl.formatMessage({ id: "mon" }),
                intl.formatMessage({ id: "tue" }),
                intl.formatMessage({ id: "wed" }),
                intl.formatMessage({ id: "thu" }),
                intl.formatMessage({ id: "fri" }),
                intl.formatMessage({ id: "sat" }),
                intl.formatMessage({ id: "sun" }),
              ].includes(row.dayOfMonth) ? (
                <strong>{row.dayOfMonth}</strong>
              ) : (
                row.dayOfMonth
              )}
            </Card.Grid>
          ))}
        </Card>
      </>
    );
  }

  return selected ? (
    <>
      <div className="calendar-view-toggle">
        <Segmented
          value={viewMode}
          onChange={setViewMode}
          options={[
            {
              label: intl.formatMessage({ id: "calendar-view-monthly" }),
              value: "monthly",
            },
            {
              label: intl.formatMessage({ id: "calendar-view-yearly" }),
              value: "yearly",
            },
          ]}
        />
      </div>
      <div className="legend">
        {[
          {
            id: 0,
            color: CALENDAR_COLORS[REACT_APP_BRAND_NAME]["available"],
            label: intl.formatMessage({ id: "calendar-free" }),
          },
          {
            id: 1,
            color: CALENDAR_COLORS[REACT_APP_BRAND_NAME]["booked"],
            label: intl.formatMessage({ id: "calendar-booked" }),
          },
          {
            id: 2,
            color: CALENDAR_COLORS[REACT_APP_BRAND_NAME]["unavailable"],
            label: intl.formatMessage({ id: "calendar-blocked" }),
          },
          {
            id: 3,
            color: CALENDAR_COLORS[REACT_APP_BRAND_NAME]["owner"],
            label: intl.formatMessage({ id: "calendar-owners" }),
          },
          {
            id: 4,
            color: CALENDAR_COLORS[REACT_APP_BRAND_NAME]["owner-tentative"],
            label: intl.formatMessage({ id: "calendar-owners-tentative" }),
          },
        ].map((i) => (
          <div key={i.id} className="legend-item">
            <div
              className="legend-item-color-box"
              style={{
                backgroundColor: i.color,
                height: 20,
                width: 20,
                marginRight: 5,
                border: "1px solid black",
              }}
            />
            <span>{i.label}</span>
          </div>
        ))}
      </div>
      {viewMode === "yearly" ? (
        <div className="calendar-yearly-title">{period.year}</div>
      ) : null}
      <Spin spinning={viewMode === "yearly" && yearlyLoading}>
        <div
          className={viewMode === "yearly" ? "calendar-yearly-grid" : undefined}
        >
          {renderedPeriods.map((p) => (
            <div
              key={p.toString()}
              style={
                viewMode === "yearly" && isCurrent(p)
                  ? {
                      border: `3px solid ${COLORS[REACT_APP_BRAND_NAME]}`,
                      borderRadius: 8,
                      padding: 4,
                    }
                  : undefined
              }
            >
              {renderPeriodCalendar(p, bookingsToRender, viewMode === "yearly")}
            </div>
          ))}
        </div>
      </Spin>
    </>
  ) : (
    <Empty
      className="empty-sign"
      description={intl.formatMessage({ id: "select-rental" })}
    />
  );
}
